import { userInfo } from "node:os";
import process from "node:process";
import {
  DAEMON_SERVICE_RUNTIME_AUTO_START_ENV,
  DAEMON_SERVICE_RUNTIME_CONTROLLER_ENV,
  DAEMON_SERVICE_RUNTIME_DETACHED_ENV,
  DAEMON_SERVICE_RUNTIME_MODE_ENV,
  DAEMON_SERVICE_RUNTIME_SYSTEM_SERVICE_ENV,
  daemonRuntimeRootPath,
} from "../../config/daemon-state";
import {
  WAKELOOP_HOME_ENV_KEY,
  wakeLoopManagedVersionBinaryPath,
} from "../../config/paths";
import {
  resolveWakeLoopEnvironmentTargetFromEnv,
  type WakeLoopEnvironmentTarget,
} from "../../config/wakeloop-environment-target";
import { resolveCliSelfVersion } from "../../local-components/managed-install";
import {
  resolveWakeLoopDistribution,
  type WakeLoopDistribution,
} from "../../runtime/distribution";
import {
  daemonServiceExecutableWrapperPath,
  daemonServiceLauncherPath,
  writeDaemonServiceExecutableWrapper,
  writeDaemonServiceLauncher,
} from "./daemon-service-bundle";
import type {
  DaemonServiceCommandSpec,
  DaemonServiceManager,
} from "./daemon-service-manager";

const DEFAULT_LAUNCHD_PATH = "/usr/bin:/bin:/usr/sbin:/sbin";
const MACOS_SYSTEM_SHELL = "/bin/sh";
const DAEMON_SERVICE_ENV_KEY_WHITELIST = new Set([
  "HOME",
  "PATH",
  "SHELL",
  "TERM",
  "TMPDIR",
  "LANG",
  "LC_ALL",
  "LC_COLLATE",
  "LC_CTYPE",
  "LC_MESSAGES",
  "LC_MONETARY",
  "LC_NUMERIC",
  "LC_TIME",
  "USER",
  "LOGNAME",
  WAKELOOP_HOME_ENV_KEY,
  "WAKELOOP_DEV_MODE",
  "WAKELOOP_ENV_PRESET",
  "WAKELOOP_USER_FACING_CLI_COMMAND",
]);

export function buildDaemonSystemServiceCommandSpec(input: {
  controller: DaemonServiceManager["controller"];
  binaryExecutablePath?: string;
  distribution?: WakeLoopDistribution;
  environmentTarget?: WakeLoopEnvironmentTarget;
}): DaemonServiceCommandSpec {
  const args: string[] = ["service", "run", "--mode", "foreground"];
  const environmentTarget =
    input.environmentTarget ?? resolveWakeLoopEnvironmentTargetFromEnv();
  const { executable, specArgs } = resolveDaemonServiceExecutable({
    args,
    binaryExecutablePath: input.binaryExecutablePath,
    controller: input.controller,
    distribution: input.distribution,
  });

  return {
    executable,
    args: specArgs,
    env: {
      ...buildSystemServiceEnvironment(),
      WAKELOOP_ENV_PRESET: environmentTarget.preset,
      WAKELOOP_GATEWAY_URL: environmentTarget.gatewayUrl,
      WAKELOOP_AUTH_URL: environmentTarget.authBaseUrl,
      WAKELOOP_USER_FACING_CLI_COMMAND: environmentTarget.userFacingCommand,
      [DAEMON_SERVICE_RUNTIME_MODE_ENV]: "foreground",
      [DAEMON_SERVICE_RUNTIME_DETACHED_ENV]: "1",
      [DAEMON_SERVICE_RUNTIME_SYSTEM_SERVICE_ENV]: "1",
      [DAEMON_SERVICE_RUNTIME_CONTROLLER_ENV]: input.controller,
      [DAEMON_SERVICE_RUNTIME_AUTO_START_ENV]: "1",
    },
    workingDirectory: daemonRuntimeRootPath(),
  };
}

function resolveDaemonServiceExecutable(input: {
  args: string[];
  binaryExecutablePath?: string;
  controller: DaemonServiceManager["controller"];
  distribution?: WakeLoopDistribution;
}): { executable: string; specArgs: string[] } {
  if ((input.distribution ?? resolveWakeLoopDistribution()) === "binary") {
    // Binary distribution: launchd/systemd/SCM run the managed binary directly
    // via `service run`; there is no launcher.mjs / wrapper middle layer.
    return {
      executable:
        input.binaryExecutablePath ??
        wakeLoopManagedVersionBinaryPath(resolveCliSelfVersion()),
      specArgs: input.args,
    };
  }
  const launcherPath = daemonServiceLauncherPath();
  if (input.controller === "launchd") {
    return {
      executable: MACOS_SYSTEM_SHELL,
      specArgs: [daemonServiceExecutableWrapperPath(), ...input.args],
    };
  }
  return {
    executable: process.execPath,
    specArgs: [launcherPath, ...input.args],
  };
}

export async function ensureDaemonSystemServiceCommandFiles(input: {
  controller: DaemonServiceManager["controller"];
  distribution?: WakeLoopDistribution;
}): Promise<void> {
  if ((input.distribution ?? resolveWakeLoopDistribution()) === "binary") {
    // No launcher/wrapper in binary mode; command-spec points at the binary.
    return;
  }
  await writeDaemonServiceLauncher();
  if (input.controller === "launchd") {
    await writeDaemonServiceExecutableWrapper();
  }
}

function buildSystemServiceEnvironment(): Record<string, string> {
  const env: Record<string, string> = {};
  const runtimeUser = userInfo().username;
  for (const [key, value] of Object.entries(process.env)) {
    if (
      typeof value !== "string" ||
      value.length === 0 ||
      !isDaemonServiceEnvironmentKey(key)
    ) {
      continue;
    }
    env[key] = sanitizeDaemonServiceEnvironmentValue(value);
  }
  if (process.platform === "darwin") {
    env.PATH ??= DEFAULT_LAUNCHD_PATH;
  }
  env.HOME ??= userInfo().homedir;
  env.USER ??= env.LOGNAME ?? runtimeUser;
  env.LOGNAME ??= env.USER;
  return env;
}

function isDaemonServiceEnvironmentKey(key: string): boolean {
  return DAEMON_SERVICE_ENV_KEY_WHITELIST.has(key);
}

function sanitizeDaemonServiceEnvironmentValue(value: string): string {
  let sanitized = "";
  for (const char of value) {
    const code = char.codePointAt(0);
    if (code === undefined) {
      continue;
    }
    if ((code >= 0x00 && code <= 0x1f) || code === 0x7f) {
      continue;
    }
    sanitized += char;
  }
  return sanitized;
}
